import { useState, useEffect, useMemo, useCallback } from "react";
import { useGetAllRoles } from "../../../../store/query/roleQuery";
import {
  useCreateCustomQuestion,
  useUpdateCustomQuestion,
  useDeleteCustomQuestion,
  useGetCustomQuestionsByRole,
} from "../../../../store/query/customQuestionQuery";
import { useToast } from "../../../../contexts-hooks/hooks/useToster";

const LEVEL_OPTIONS = [
  { value: "Junior", label: "Junior" },
  { value: "Middle", label: "Middle" },
  { value: "Senior", label: "Senior" },
  { value: "Leadership", label: "Leadership" },
];

const useCreateQuestion = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [jdLevel, setJdLevel] = useState("");
  const [showQuestionInput, setShowQuestionInput] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    question: null,
  });

  const allRoles = useGetAllRoles();
  const createMutation = useCreateCustomQuestion();
  const updateMutation = useUpdateCustomQuestion();
  const deleteMutation = useDeleteCustomQuestion();
  const { showToast } = useToast();

  const rolesOptions = allRoles?.data?.data || [];

  const { data: fetchedQuestionsData, isLoading: isLoadingQuestions } =
    useGetCustomQuestionsByRole({
      role: selectedRole?.role,
      level: jdLevel,
      page,
      limit: rowsPerPage,
    });

  const questionsList = useMemo(() => {
    if (!selectedRole || !fetchedQuestionsData?.data?.questions) {
      return [];
    }
    return fetchedQuestionsData.data.questions.map((q) => ({
      id: q._id,
      question: q.question,
    }));
  }, [selectedRole, fetchedQuestionsData]);

  const totalPages = fetchedQuestionsData?.data?.totalPages || 1;

  useEffect(() => {
    setPage(1);
  }, [selectedRole, jdLevel]);

  useEffect(() => {
    if (!jdLevel) {
      setShowQuestionInput(false);
    }
  }, [jdLevel]);

  const normalizeQuestion = useCallback((q) => {
    if (!q) return "";
    return String(q)
      .replace(/^\d+\.\s*/, "")
      .replace(/[?!.]+$/, "")
      .replace(/[^\w\s]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }, []);

  const isQuestionDuplicate = useCallback(
    (questionText, excludeId = null) => {
      const normalizedNew = normalizeQuestion(questionText);
      for (const q of questionsList) {
        if (
          q.id !== excludeId &&
          normalizeQuestion(q.question) === normalizedNew
        ) {
          return true;
        }
      }
      return false;
    },
    [questionsList, normalizeQuestion],
  );

  const resetInput = () => {
    setQuestionText("");
    setEditingQuestion(null);
    setError("");
    setShowQuestionInput(false);
  };
  const clearInput = () => {
    setQuestionText("");
    setEditingQuestion(null);
    setError("");
  };
  const handleRoleChange = (_event, value) => {
    setSelectedRole(value);
    resetInput();
  };

  const handleCreateQuestionClick = () => {
    if (selectedRole && jdLevel) {
      setShowQuestionInput(true);
    }
  };

  const handleQuestionChange = (event) => {
    setQuestionText(event.target.value);
    if (error) {
      setError("");
    }
  };

  const handleSaveQuestion = async () => {
    const trimmedQuestion = questionText.trim().replace(/^\d+\.\s*/, "");
    if (!trimmedQuestion || !selectedRole) return;

    if (isQuestionDuplicate(trimmedQuestion, editingQuestion?.id)) {
      setError("This question already exists for this role!");
      return;
    }

    try {
      if (editingQuestion) {
        if (trimmedQuestion === editingQuestion.question.trim().replace(/^\d+\.\s*/, "")) {
          showToast("No changes to update", "info");
          return;
        }
        await updateMutation.mutateAsync({
          questionId: editingQuestion.id,
          role: selectedRole.role,
          level: jdLevel,
          question: trimmedQuestion,
        });
        showToast("Question updated successfully", "success");
        resetInput();
      } else {
        await createMutation.mutateAsync({
          role: selectedRole.role,
          level: jdLevel,
          question: trimmedQuestion,
        });
        showToast("Question added successfully", "success");
        const currentTotal = fetchedQuestionsData?.data?.totalQuestions || 0;
        const newTotal = currentTotal + 1;
        const newPage = Math.ceil(newTotal / rowsPerPage);
        if (newPage > page) {
          setPage(newPage);
        }
        clearInput();
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save question";
      showToast(errorMessage, "error");
    }
  };

  const handleEditQuestion = (questionId) => {
    const question = questionsList.find((q) => q.id === questionId);
    if (!question) return;

    setQuestionText(question.question);
    setEditingQuestion(question);
    setShowQuestionInput(true);
    setError("");
  };

  const handleDeleteClick = (questionId) => {
    const question = questionsList.find((q) => q.id === questionId);
    if (question) {
      setDeleteConfirmation({ open: true, question });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.question) return;

    try {
      await deleteMutation.mutateAsync({
        questionId: deleteConfirmation.question.id,
        role: selectedRole.role,
        level: jdLevel,
      });
      showToast("Question deleted successfully", "success");

      if (editingQuestion?.id === deleteConfirmation.question.id) {
        resetInput();
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete question";
      showToast(errorMessage, "error");
    } finally {
      setDeleteConfirmation({ open: false, question: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ open: false, question: null });
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const isSubmitting =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return {
    selectedRole,
    jdLevel,
    setJdLevel,
    showQuestionInput,
    questionText,
    questionsList,
    rolesOptions,
    isLoadingRoles: allRoles.isLoading,
    isLoadingQuestions,
    isSubmitting,
    error,
    isEditing: !!editingQuestion,
    handleRoleChange,
    handleCreateQuestionClick,
    handleQuestionChange,
    handleSaveQuestion,
    handleCancelQuestion: resetInput,
    handleEditQuestion,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    deleteConfirmation,
    levelOptions: LEVEL_OPTIONS,
    page,
    rowsPerPage,
    isFirstPage: page === 1,
    isLastPage: page >= totalPages,
    handlePrevPage,
    handleNextPage,
    handleRowsPerPageChange,
  };
};

export default useCreateQuestion;
